import {
  commandRequest,
  type UiCommandRequest,
} from '../../../agents/context/ui-command-registry.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { Sparkles, Command } from 'lucide-react';
import type { ReactElement } from 'react';

interface ChatWelcomeHeroProps {
  onSelectPrompt: (command: UiCommandRequest) => void;
  disabled?: boolean;
}

export function ChatWelcomeHero({
  onSelectPrompt,
  disabled = false,
}: ChatWelcomeHeroProps): ReactElement {
  useI18nSubscription();

  const playbooks: { id: string; title: string; action: UiCommandRequest }[] = [
    {
      id: 'pipeline-audit',
      title: i18n.t('crm.ai_chat.welcome.pipeline_audit_title'),
      action: commandRequest('pipelineAudit'),
    },
    {
      id: 'hot-leads',
      title: i18n.t('crm.ai_chat.welcome.hot_leads_title'),
      action: commandRequest('scenarioHotLeads'),
    },
    {
      id: 'follow-up',
      title: i18n.t('crm.ai_chat.welcome.follow_up_title'),
      action: commandRequest('scenarioFollowUp'),
    },
    {
      id: 'manager-brief',
      title: i18n.t('crm.ai_chat.welcome.manager_brief_title'),
      action: commandRequest('executiveBrief'),
    },
  ];

  return (
    <div
      data-testid="crm-chat-welcome-hero"
      className="flex flex-col items-center justify-center py-6 px-4 text-center max-w-2xl mx-auto my-auto select-none"
    >
      {/* GovardOS Emblem */}
      <div className="relative mb-4">
        <div className="absolute -inset-1.5 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 opacity-25 dark:opacity-40 blur-md animate-pulse" />
        <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-white shadow-sm dark:border-amber-900/30 dark:from-amber-950/40 dark:to-stone-800">
          <Sparkles size={26} className="text-amber-600 dark:text-amber-500" />
        </div>
      </div>

      {/* Main Headline */}
      <h2 className="text-xl font-bold tracking-tight text-stone-900 dark:text-stone-100 sm:text-2xl mb-1.5">
        {i18n.t('crm.ai_chat.welcome.title')}
      </h2>
      <p className="text-xs text-stone-500 dark:text-stone-300 max-w-lg mb-6 leading-relaxed">
        {i18n.t('crm.ai_chat.welcome.subtitle')}
      </p>

      {/* Try Asking Chips */}
      <div className="w-full max-w-md">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-300">
          {i18n.t('crm.ai_chat.welcome.try_asking')}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {playbooks.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={disabled}
              onClick={() => onSelectPrompt(item.action)}
              className="cursor-pointer rounded-full border border-stone-200/90 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 shadow-2xs transition hover:border-amber-300 hover:bg-amber-50/60 hover:text-amber-900 disabled:opacity-50 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300 dark:hover:border-amber-700 dark:hover:bg-amber-950/30 dark:hover:text-amber-200"
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>

      {/* Keyboard Slash Command Hint */}
      <div className="mt-6 flex items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-300 font-medium">
        <Command size={11} className="text-stone-500 dark:text-stone-300" />
        <span>
          {i18n.t('crm.ai_chat.slash.title')}: {i18n.t('crm.ai_chat.slash.hint_prefix')}{' '}
          <code className="rounded bg-stone-100 px-1 py-0.5 font-mono text-[10px] text-stone-600 font-bold dark:bg-stone-800 dark:text-stone-400">
            /
          </code>{' '}
          {i18n.t('crm.ai_chat.slash.hint_suffix')}
        </span>
      </div>
    </div>
  );
}
